import React, { useState, useEffect, useContext, useCallback } from "react";
import { WdContext } from "../../context/WdProvider";
import { documentAPI } from "../../api";
import WplInput from "../wpl-input/WplInput";
import WplButton from "../wpl-button/WplButton";
import Card from "../card/Card";
import WplFileUpload from "../wpl-file-upload/WplFileUpload";
import deleteIconBlack from "../../assets/img/delete_black.svg";
import deleteIconWhite from "../../assets/img/delete_white.svg";
import "./document-card.css";
import "../wpl-table/wpl-table.css";

export default function DocumentCard({ windfarm, turbine }) {
  const {
    documents,
    uploadDocument,
    deleteDocument,
    getTurbineDocuments,
    getWindfarmDocuments,
  } = useContext(WdContext);

  const [searchText, setSearchText] = useState("");
  const [downloadingAll, setDownloadingAll] = useState(false);
  useEffect(() => {
    if (!windfarm) return;
    if (turbine) {
      getTurbineDocuments(turbine.id);
    } else {
      getWindfarmDocuments(windfarm.id);
    }
  }, [turbine, windfarm]);

  const open_preview = useCallback((id) => {
    documentAPI.download(id).then((url) => {
      window.open(url, "_blank");
    });
  }, []);

  const download_document = useCallback((doc) => {
    documentAPI.download(doc.id).then((url) => {
      fetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = objectUrl;
          anchor.download = doc.file_name;
          anchor.click();
          URL.revokeObjectURL(objectUrl);
        })
        .catch(() => {
          // The signed URL may not allow cross-origin reads; open it instead
          window.open(url, "_blank");
        });
    });
  }, []);

  const download_all = useCallback(() => {
    if (!windfarm || downloadingAll) return;
    setDownloadingAll(true);
    documentAPI
      .downloadAllWindfarm(windfarm.id)
      .then((url) => {
        window.open(url, "_blank");
      })
      .catch((err) => {
        console.error("Failed to download all documents", err);
        window.alert(
          "Sorry, we couldn't prepare the download. Please try again, or contact us at contact@windpowerlab.com if the problem continues."
        );
      })
      .finally(() => setDownloadingAll(false));
  }, [windfarm, downloadingAll]);

  const delete_document = useCallback((document) => {
    if (!window.confirm(`Delete ${document.file_name} permanently?`)) return;
    deleteDocument(document.id);
  }, []);

  const upload_document = useCallback(
    (file_name, content, callback) => {
      uploadDocument({
        file_name,
        content,
        windfarm_id: windfarm?.id,
        turbine_id: turbine?.id,
        callback,
      });
      return true;
    },
    [windfarm, turbine]
  );

  if (!windfarm && !turbine) return null;
  return (
    <Card className="document-card">
      <div className="header-wrapper">
        <div>
          <h1>Reports</h1>
          {windfarm && !turbine && <p>{windfarm.name} Documents</p>}
          {turbine && (
            <p>
              {windfarm.name} - {turbine.name} Documents
            </p>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {windfarm && !turbine && documents.length > 0 && (
            <WplButton
              value={downloadingAll ? "Preparing…" : "Download all"}
              loading={downloadingAll}
              onClick={download_all}
            />
          )}
          <WplFileUpload didSelectFile={upload_document} />
        </div>
      </div>
      <WplInput
        placeholder="Search documents"
        className="search-bar"
        value={searchText}
        onChanged={setSearchText}
      />
      <div className="table-wrap">
        <table className="document-table">
          <tbody>
            {documents
              .filter(
                (d) =>
                  !searchText ||
                  d.file_name
                    .toLowerCase()
                    .indexOf(searchText.toLowerCase()) !== -1
              )
              .sort((a, b) => a.file_name.localeCompare(b.file_name))
              .map((doc) => {
                const searchStart =
                  !searchText ||
                  doc.file_name.toLowerCase().indexOf(searchText.toLowerCase());
                const dotIndex = doc.file_name.lastIndexOf(".");
                const baseName =
                  dotIndex > 0
                    ? doc.file_name.substring(0, dotIndex)
                    : doc.file_name;
                const extension =
                  dotIndex > 0
                    ? doc.file_name.substring(dotIndex + 1).toUpperCase()
                    : null;
                const rawTimestamp = String(doc.timestamp_created || "");
                const uploadedDate = new Date(
                  /^\d{4}-\d{2}-\d{2} /.test(rawTimestamp)
                    ? rawTimestamp.replace(" ", "T")
                    : rawTimestamp
                );
                const uploaded = isNaN(uploadedDate)
                  ? null
                  : uploadedDate.toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                return (
                  <tr key={doc.id} onClick={() => open_preview(doc.id)}>
                    <td className="doc-icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                          fill="currentColor"
                          d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"
                        />
                      </svg>
                    </td>
                    <td className="doc-title" title="Open preview">
                      <span className="doc-name">
                        {baseName.substring(0, searchStart)}
                        <span className="found">
                          {baseName.substring(
                            searchStart,
                            searchStart + searchText.length
                          )}
                        </span>
                        {baseName.substring(searchStart + searchText.length)}
                      </span>
                      <span className="preview-hint">Open preview</span>
                      {(extension || uploaded) && (
                        <span className="doc-meta">
                          {[extension, uploaded].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </td>
                    <td
                      className="doc-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        download_document(doc);
                      }}
                    >
                      <div className="icon-button download-btn" title="Download">
                        <svg
                          viewBox="0 0 24 24"
                          role="img"
                          aria-label={`download document: ${doc.file_name}`}
                        >
                          <path
                            fill="currentColor"
                            d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z"
                          />
                        </svg>
                      </div>
                    </td>
                    <td
                      className="doc-action"
                      onClick={(e) => {
                        e.stopPropagation();
                        delete_document(doc);
                      }}
                    >
                      <div className="icon-button delete-btn" title="Delete">
                        <img
                          src={deleteIconBlack}
                          className="black"
                          alt={`delete document: ${doc.file_name}`}
                        />
                        <img
                          src={deleteIconWhite}
                          className="white"
                          alt={`delete document: ${doc.file_name}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            {documents.length === 0 && (
              <tr>
                <td colSpan={4}>No files</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
